import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  useTheme
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  AutoAwesome as AgentIcon,
  Bolt as LightningIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { Agent } from '../../types/agent';
import WhatsAppStatusIcon from '../WhatsAppStatusIcon';
import toast from 'react-hot-toast';
import { agentApi } from '../../utils/agentApi';
import { fetchConversationCounts } from '../../utils/chatHistory';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';
import { useTranslation } from 'react-i18next';

interface AgentsListProps {
  agents: Agent[];
  loading: boolean;
  onEdit: (agentId: string) => void;
  onView: (agentId: string) => void;
  onDuplicate: (agentId: string) => void;
  onDelete: (agentId: string) => void;
  onRefresh: () => void;
}

// Componente auxiliar para mostrar ícone de bloqueio quando IA está bloqueada
const AIBlockedIndicator: React.FC<{ agent: Agent }> = ({ agent }) => {
  const { t } = useTranslation();
  const connection = useConnectionStatus({ agentId: agent.id, agent });

  if (!agent.shouldBlockAI) return null;

  const isConnected = connection.status.zapi.isConnected || connection.status.whatsappNative.isConnected;

  if (!isConnected) return null;
  
  return (
    <Tooltip title={t('agents.aiBlocked')}>
      <BlockIcon
        sx={{
          color: 'error.main',
          fontSize: '1.2rem'
        }}
      />
    </Tooltip>
  );
};

const AgentsList: React.FC<AgentsListProps> = ({
  agents,
  loading,
  onEdit,
  onView,
  onDuplicate,
  onDelete,
  onRefresh
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [operationLoading, setOperationLoading] = useState<string | null>(null);
  const [conversationCounts, setConversationCounts] = useState<Record<string, number>>({});

  useEffect(() => {
    let cancelled = false;
    async function loadCounts() {
      if (!agents || agents.length === 0) {
        setConversationCounts({});
        return;
      }
      try {
        const ids = agents.map((a) => a.id);
        const map = await fetchConversationCounts(ids);
        if (!cancelled) setConversationCounts(map);
      } catch {
        if (!cancelled) setConversationCounts({});
      }
    }
    loadCounts();
    return () => {
      cancelled = true;
    };
  }, [agents]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, agent: Agent) => {
    setAnchorEl(event.currentTarget);
    setSelectedAgent(agent);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAgent(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAgent) return;

    try {
      setOperationLoading('delete');
      await agentApi.deleteAgent(selectedAgent.id);
      onDelete(selectedAgent.id);
      setDeleteDialog(false);
      handleMenuClose();
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error(t('agents.errors.deleteError'));
    } finally {
      setOperationLoading(null);
    }
  };

  const handleDuplicate = async (agent: Agent) => {
    try {
      setOperationLoading(`duplicate-${agent.id}`);
      await onDuplicate(agent.id);
      handleMenuClose();
    } catch (error) {
      console.error('Error duplicating agent:', error);
      toast.error(t('agents.errors.duplicateError'));
    } finally {
      setOperationLoading(null);
    }
  };

  const getStatusColor = (status: boolean): 'success' | 'error' | 'warning' | 'default' => {
    return status ? 'success' : 'error';
  };

  const getStatusText = (status: boolean): string => {
    return status ? t('agents.active') : t('agents.inactive');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (agents.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <AgentIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {t('agents.noAgents')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('agents.createFirst')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)'
          },
          gap: 3
        }}
      >
        {agents.map((agent) => (
          <Card
            key={agent.id}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: 6
              }
            }}
          >
            <CardContent sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {agent.name}
                  </Typography>
                  {agent.quickAgent && (
                    <Tooltip title={t('agents.quickAgent')}>
                      <LightningIcon
                        sx={{
                          color: 'warning.main',
                          fontSize: '1.2rem',
                          animation: 'pulse 2s infinite'
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WhatsAppStatusIcon agentId={agent.id} agent={agent} size="small" />
                  <AIBlockedIndicator agent={agent} />
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, agent)}
                    disabled={operationLoading !== null}
                  >
                    {operationLoading === `duplicate-${agent.id}` ? (
                      <CircularProgress size={20} />
                    ) : (
                      <MoreIcon />
                    )}
                  </IconButton>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip
                  label={getStatusText(agent.active)}
                  color={getStatusColor(agent.active)}
                  size="small"
                  sx={{
                    color: theme.palette.mode === 'dark' ? '#000' : undefined
                  }}
                />
                <Chip
                  label={agent.model}
                  variant="outlined"
                  size="small"
                />
              </Box>

              <Typography variant="caption" color="text.secondary">
                {t('agents.prompts')}: {agent.prompts?.length || 0} •
                {t('agents.history')}: {conversationCounts[agent.id] ?? 0} {t('agents.conversations')}
              </Typography>
            </CardContent>

            <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
              <Button
                size="small"
                startIcon={<ViewIcon />}
                onClick={() => onView(agent.id)}
              >
                {t('common.view')}
              </Button>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => onEdit(agent.id)}
              >
                {t('common.edit')}
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>

      {/* Menu de contexto */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedAgent && onView(selectedAgent.id)}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.view')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => selectedAgent && onEdit(selectedAgent.id)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common.edit')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => selectedAgent && handleDuplicate(selectedAgent)}>
          <ListItemIcon>
            <DuplicateIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('agents.duplicate')}</ListItemText>
        </MenuItem>

        {/* <MenuItem onClick={handleOpenClearHistory}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('agents.clearHistory')}</ListItemText>
        </MenuItem> */}

        <MenuItem onClick={() => setDeleteDialog(true)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('common.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Dialog de confirmação para deletar */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>{t('agents.deleteConfirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('agents.deleteConfirm.message', { name: selectedAgent?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={operationLoading === 'delete'}
          >
            {operationLoading === 'delete' ? <CircularProgress size={20} /> : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AgentsList;